// -- type definitions --

/** Internal axis key reserved for scatter plot x axes. */
export const SCATTER_X_AXIS_KEY = '__scatter_x__' as const;

export type ScatterXAxisKey = typeof SCATTER_X_AXIS_KEY;

// -- helper functions --

/**
 * Returns whether a source occupies the scatter plot x-axis slot.
 *
 * @param areSourcesForScatterPlot - Whether the plot is in scatter mode.
 * @param sourceIndex - Zero-based index of the source in the input array.
 * @param sourceCount - Total number of sources in the input array.
 * @returns `true` when the source is the last one in a scatter plot.
 */
export function isScatterXAxisSource(
  areSourcesForScatterPlot: boolean,
  sourceIndex: number,
  sourceCount: number
): boolean {
  return areSourcesForScatterPlot && sourceIndex === sourceCount - 1;
}

/**
 * Resolves the axis key for a source during plot preference initialization.
 *
 * Scatter x sources receive a dedicated internal key so they never collide
 * with y-axis keys derived from `measurement_unit`.
 *
 * @param measurementUnit - Unit string used to group y axes.
 * @param areSourcesForScatterPlot - Whether the plot is in scatter mode.
 * @param sourceIndex - Zero-based index of the source in the input array.
 * @param sourceCount - Total number of sources in the input array.
 * @returns The axis key to assign to the source.
 */
export function resolveAxisKeyForSource(
  measurementUnit: string,
  areSourcesForScatterPlot: boolean,
  sourceIndex: number,
  sourceCount: number
): string {
  return isScatterXAxisSource(
    areSourcesForScatterPlot,
    sourceIndex,
    sourceCount
  )
    ? SCATTER_X_AXIS_KEY
    : measurementUnit;
}
